"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useWidgetFilters = void 0;
var tslib_1 = require("tslib");
// (C) 2020-2023 GoodData Corporation
var react_1 = require("react");
var sdk_model_1 = require("@gooddata/sdk-model");
var json_stable_stringify_1 = tslib_1.__importDefault(require("json-stable-stringify"));
var compact_1 = tslib_1.__importDefault(require("lodash/compact"));
var first_1 = tslib_1.__importDefault(require("lodash/first"));
var flow_1 = tslib_1.__importDefault(require("lodash/flow"));
var isEqual_1 = tslib_1.__importDefault(require("lodash/isEqual"));
var sortBy_1 = tslib_1.__importDefault(require("lodash/fp/sortBy"));
var model_1 = require("../../../model");
var safeSerializeObjRef_1 = require("../../../_staging/metadata/safeSerializeObjRef");
/**
 * Hook for obtaining the effective filters for a widget.
 *
 * @remarks
 * The filters returned should be used with {@link @gooddata/sdk-model#insightSetFilters} to obtain
 * insight that is ready for execution.
 *
 * @param widget - widget to get effective filters for
 * @param insight - insight to evaluate the filters for in context of the widget
 * @returns set of filters that should be used to execute the given widget
 *
 * @public
 */
function useWidgetFilters(widget, insight) {
    var _a = tslib_1.__read(react_1.useState({
        filters: [],
        filterQueryStatus: "pending",
    }), 2), effectiveFiltersState = _a[0], setEffectiveFiltersState = _a[1];
    var _b = useNonIgnoredFilters(widget), nonIgnoredFiltersStatus = _b.status, nonIgnoredFiltersError = _b.error, nonIgnoredFilters = _b.result;
    var _c = model_1.useDashboardQueryProcessing({
        queryCreator: model_1.queryWidgetFilters,
        onSuccess: function (filters) {
            setEffectiveFiltersState({
                filters: filters,
                filterQueryStatus: "success",
            });
        },
        onBeforeRun: function () {
            setEffectiveFiltersState({
                filters: [],
                filterQueryStatus: "running",
            });
        },
        onRejected: function () {
            setEffectiveFiltersState({
                filters: [],
                filterQueryStatus: "rejected",
            });
        },
        onError: function () {
            setEffectiveFiltersState({
                filters: [],
                filterQueryStatus: "error",
            });
        },
    }), runFiltersQuery = _c.run, status = _c.status, error = _c.error;
    // only run the "full" filters query if any of the non-ignored filters has changed
    react_1.useEffect(function () {
        if ((widget === null || widget === void 0 ? void 0 : widget.ref) && nonIgnoredFiltersStatus === "success") {
            runFiltersQuery(widget.ref, insight);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        safeSerializeObjRef_1.safeSerializeObjRef(widget === null || widget === void 0 ? void 0 : widget.ref),
        fullFiltersDigest(nonIgnoredFilters),
        insight,
        nonIgnoredFiltersStatus,
        widget === null || widget === void 0 ? void 0 : widget.dateDataSet,
    ]);
    return {
        result: effectiveFiltersState.filters,
        status: combineQueryProcessingStatuses(nonIgnoredFiltersStatus, effectiveFiltersState.filterQueryStatus, status),
        error: nonIgnoredFiltersError !== null && nonIgnoredFiltersError !== void 0 ? nonIgnoredFiltersError : error,
    };
}
exports.useWidgetFilters = useWidgetFilters;
/**
 * Hook that retrieves the non-ignored dashboard level filters for a widget.
 *
 * @param widget - widget to get the non-ignored filters for
 */
function useNonIgnoredFilters(widget) {
    var dashboardFilters = model_1.useDashboardSelector(model_1.selectFilterContextFilters);
    var isInEditMode = model_1.useDashboardSelector(model_1.selectIsInEditMode);
    var widgetIgnoresDateFilter = !(widget === null || widget === void 0 ? void 0 : widget.dateDataSet);
    var _a = tslib_1.__read(react_1.useState([]), 2), nonIgnoredFilterRefs = _a[0], setNonIgnoredFilterRefs = _a[1];
    /**
     * This handles cases where:
     * - the set of filters themselves has changed (new filters added/filters removed)
     * - widget itself has changed (potentially changing the ignore settings)
     *
     * Those are the only ways how the set of non-ignored filters can change.
     */
    var _b = model_1.useDashboardQueryProcessing({
        queryCreator: model_1.queryWidgetFilters,
        onSuccess: function (result) {
            setNonIgnoredFilterRefs(function (prevValue) {
                // only set state if the values really changed
                // this prevents the full query from running unnecessarily
                if (!isEqual_1.default(prevValue, result)) {
                    return result.map(sdk_model_1.filterObjRef);
                }
                return prevValue;
            });
        },
    }), run = _b.run, status = _b.status, error = _b.error;
    react_1.useEffect(function () {
        if (widget === null || widget === void 0 ? void 0 : widget.ref) {
            // force ignore the insight -> this way we get only the dashboard level filters even for InsightWidgets
            run(widget.ref, null);
        }
    }, [
        safeSerializeObjRef_1.safeSerializeObjRef(widget === null || widget === void 0 ? void 0 : widget.ref),
        json_stable_stringify_1.default(widget === null || widget === void 0 ? void 0 : widget.ignoreDashboardFilters),
        filtersDigest(dashboardFilters, widgetIgnoresDateFilter, isInEditMode),
    ]);
    var nonIgnoredFilters = react_1.useMemo(function () {
        return dashboardFilters.filter(function (filter) {
            if (sdk_model_1.isDashboardAttributeFilter(filter)) {
                return nonIgnoredFilterRefs.some(function (validRef) {
                    return sdk_model_1.areObjRefsEqual(validRef, filter.attributeFilter.displayForm);
                });
            }
            else {
                return !widgetIgnoresDateFilter;
            }
        });
    }, [dashboardFilters, nonIgnoredFilterRefs, widgetIgnoresDateFilter]);
    return {
        error: error,
        status: status,
        result: nonIgnoredFilters,
    };
}
// the lower the number, the more priority the status has
var statusPriorities = {
    error: 0,
    rejected: 1,
    running: 2,
    success: 3,
    pending: 4,
};
function combineQueryProcessingStatuses() {
    var _a;
    var statuses = [];
    for (var _i = 0; _i < arguments.length; _i++) {
        statuses[_i] = arguments[_i];
    }
    return ((_a = flow_1.default(compact_1.default, sortBy_1.default(function (status) { return statusPriorities[status]; }), first_1.default)(statuses)) !== null && _a !== void 0 ? _a : "pending");
}
/**
 * Gets a simplified serialized digest of the filters provided. This is useful for detecting if the set of filters has changed.
 *
 * @remarks
 * This digest is only concerned with the display forms/datasets, not the selected values of the filters
 * (it does, however, ignore noop attribute filters).
 * Also, the order of filters is ignored as it does not matter for executions.
 *
 * @param filters - filters to get digest for
 * @param ignoreDateFilter - whether to ignore date filters
 * @param isInEditMode - whether or not we are in edit mode
 * @returns
 */
function filtersDigest(filters, ignoreDateFilter, isInEditMode) {
    return filters
        .filter(function (filter) {
        if (sdk_model_1.isDashboardAttributeFilter(filter)) {
            /**
             * Remove noop attribute filters in edit mode as they would cause a useless query when a new filter (noop by default) is added.
             * Keep them in view mode so that switching to and from All on an ignored filter does not show loading.
             * This is a tradeoff so that we optimize for the view mode performance and also keep the more frequent use case in edit mode
             * (adding a new filter) loading-free as well. Switching to and from All in edit mode will still show loading, but have no way
             * of telling whether a noop filter is noop because it was just added or because it was set that way by the user.
             */
            var isNoop = filter.attributeFilter.negativeSelection &&
                sdk_model_1.attributeElementsIsEmpty(filter.attributeFilter.attributeElements);
            return !isNoop || !isInEditMode;
        }
        else {
            // if the widget ignores date filters, remove it from the digest to avoid false positives
            // when date filter changes to or from All time (this effectively adds/removes the date filter in the filters set,
            // but we do not care either way, so remove it altogether)
            return !ignoreDateFilter;
        }
    })
        .map(function (filter) {
        return sdk_model_1.isDashboardAttributeFilter(filter)
            ? "df_" + safeSerializeObjRef_1.safeSerializeObjRef(filter.attributeFilter.displayForm)
            : "ds_" + safeSerializeObjRef_1.safeSerializeObjRef(filter.dateFilter.dataSet);
    })
        .sort()
        .join("|");
}
/**
 * Gets a serialized digest of the filters provided. This is useful for detecting if the set of filters has changed.
 *
 * @remarks
 * This digest also takes the selected values of the filters into account.
 * The order of filters is ignored as it does not matter for executions.
 *
 * @param filters - filters to get digest for
 * @returns
 */
function fullFiltersDigest(filters) {
    return filters
        .map(function (filter) { return json_stable_stringify_1.default(filter); })
        .sort()
        .join("|");
}
//# sourceMappingURL=useWidgetFilters.js.map